import {
  Text,
  Flex,
  Spinner,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Box,
} from "@chakra-ui/react";
import { useQuery, useQueryClient, skipToken } from "@tanstack/react-query";
import { Comic, Release } from "../../../types";
import {
  getComicReleases,
  getRelease,
  useSlideInMenu,
} from "../hooks/useSlideInMenu";
import { useEffect, useMemo, useRef } from "react";
import { ReleaseInfoHeader } from "./ReleaseInfoHeader";
import { ReleaseInfoCoverCarousel } from "./ReleaseInfoCoverCarousel";
import { ReleaseInfoPageCarousel } from "./ReleaseInfoPageCarousel";
import { ShowMoreSection } from "../../containers/ShowMoreSection";
import { ArtistSection } from "../comicDetails/ArtistSection";
import { ComicContributorList } from "../comicDetails/ComicDescription";
import { ComicLabelsCarousel } from "../../comicLabels/ComicLabelsCarousel";
import { SlideInHeader } from "../components/SlideInHeader";
import { SLIDE_IN_MENUS } from "../../../screens/root/constants";
import { AnimatePresence, motion } from "framer-motion";
import { FunnelConfigTypes, FunnelContextArea } from "../../funnel/types";
import { useReleaseInfoPaywall } from "./hooks/useReleaseInfoPaywall";
import { useFunnel } from "../../funnel/hooks/useFunnel";
import { useEntityCache } from "../../../hooks/useEntityCache";
import { ReleaseInfoActions } from "./ReleaseInfoActions";
import {
  useSlideInFunnel,
  funnelAnimationVariants,
  menuAnimationVariants,
  transitionConfig,
} from "../hooks/useSlideInFunnel";

const MotionFlex = motion(Flex);
import { Bundle } from "../../../types";
import { UnderlinedTab } from "../../tabs/UnderlinedTab";
import { NewBadge } from "../../buttons/NewBadge";
import { SlideInMenuBundlesList } from "../components/SlideInMenuBundlesList";
import { t } from "i18next";
import { trackReleaseDetailView } from "../../../analytics/events/navigation";
import { mapDetailSource } from "../../../analytics/params/navigation";
import { getNavigationAnalyticsAttribution } from "../../../analytics/params/navigationContextAttribution";
import { shouldTrackDetailViewForMenuInstance } from "../../../analytics/detailViewRegistry";

interface ReleaseInfoMenuProps {
  onCloseMenu?: () => void;
  onFunnelChange?: (funnelConfig: FunnelConfigTypes | undefined) => void;
}
export function ReleaseInfoMenuWrapper(props: ReleaseInfoMenuProps) {
  return <ReleaseInfoMenu {...props} />;
}

export function ReleaseInfoMenu({
  onCloseMenu,
  onFunnelChange,
}: ReleaseInfoMenuProps) {
  const { openReleaseInfoMenu, goBack, releaseUuid, navigationContext, menuInstanceId } =
    useSlideInMenu();
  const queryClient = useQueryClient();
  const { saveEntityToCache } = useEntityCache();

  const { funnelConfig, setFunnelConfig, scrollContainerRef, handleBack } =
    useSlideInFunnel({ onFunnelChange, goBack });

  const { data: fetchedReleaseResponse, isLoading } = useQuery({
    enabled: !!releaseUuid,
    queryKey: ["releaseDetailsRelease", releaseUuid],
    networkMode: "offlineFirst",
    queryFn: async () => {
      const response = await getRelease(releaseUuid ?? "", navigationContext);
      if (response?.data.payload.results) {
        saveEntityToCache(response.data.payload.results);
      }
      return response;
    },
  });

  const fetchedRelease = fetchedReleaseResponse?.data.payload.results;
  const { data: fetchedComicReleasesResponse, isLoading: isLoadingReleases } =
    useQuery({
      enabled: !!fetchedRelease?.comic?.slug,
      queryKey: ["fetchedComicReleases", fetchedRelease?.comic?.slug],
      networkMode: "offlineFirst",
      placeholderData: (prev) => prev,
      queryFn: () => {
        return getComicReleases(
          fetchedRelease?.comic?.slug ?? "",
          fetchedRelease?.lang_id,
        );
      },
    });

  const cacheKey = `${fetchedRelease?.entity_type}-${fetchedRelease?.id}`;
  const { data: activeRelease } = useQuery<Release>({
    enabled: !!fetchedRelease,
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  const releases = fetchedComicReleasesResponse?.data.payload.results;

  // Get bundles directly from the release data and convert from ImmutableObjects to regular objects
  const bundles: Bundle[] = activeRelease?.bundles
    ? activeRelease.bundles.map((bundle) => bundle as unknown as Bundle)
    : [];

  const hasBundles = bundles.length > 0;

  useEffect(() => {
    /// We need to update the cached release data from home sections
    // so that the changes get reflected
    queryClient.setQueryData(
      [`${activeRelease?.entity_type}-${activeRelease?.id}`],
      activeRelease,
    );
  }, [activeRelease, queryClient]);

  const openPaywall = useReleaseInfoPaywall({
    setFunnelConfig,
  });

  const releaseInfoFunnelConfig = useMemo(() => {
    return {
      ...funnelConfig,
      setFunnelConfig,
    };
  }, [funnelConfig, setFunnelConfig]);

  const { funnelBody, funnelFooter } = useFunnel(releaseInfoFunnelConfig ?? {});
  const lastTrackedReleaseId = useRef<number | undefined>();

  const comic = (activeRelease?.comic ?? fetchedRelease?.comic) as Comic;

  useEffect(() => {
    if (
      !activeRelease?.id ||
      !activeRelease?.comic?.id ||
      lastTrackedReleaseId.current === activeRelease.id
    ) {
      return;
    }

    if (
      !shouldTrackDetailViewForMenuInstance(
        menuInstanceId,
        "release",
        activeRelease.id,
      )
    ) {
      lastTrackedReleaseId.current = activeRelease.id;
      return;
    }

    const source = mapDetailSource(navigationContext);
    const { impressionId, searchQueryId } =
      getNavigationAnalyticsAttribution(navigationContext);
    trackReleaseDetailView({
      comicId: activeRelease.comic.id,
      releaseId: activeRelease.id,
      source,
      impressionId,
      searchQueryId,
      searchResultPosition: navigationContext?.cardPosition,
      referrerUrl:
        source === "deep_link" ? document.referrer || undefined : undefined,
    });

    lastTrackedReleaseId.current = activeRelease.id;
  }, [activeRelease?.id, activeRelease?.comic?.id, menuInstanceId, navigationContext]);

  return (
    <Flex
      flexDirection="column"
      height="100%"
      maxHeight="calc(100vh - 32px)"
      id="gcSlideInMenuContentContainer"
    >
      <SlideInHeader
        handleBack={handleBack}
        openMenu={SLIDE_IN_MENUS.releaseInfo}
        onCloseMenu={onCloseMenu}
      />
      <AnimatePresence initial={false} mode="wait">
        {funnelBody ? (
          <MotionFlex
            key="funnel"
            ref={scrollContainerRef}
            {...funnelAnimationVariants}
            transition={transitionConfig}
            className="styled-scrollbar"
            gap="24px"
            flexGrow={1}
            overflowY="auto"
            maxWidth="100%"
            flexDirection="column"
            minHeight={0}
          >
            <Box transition="padding 0.1s ease-in-out" transitionDelay={"0.1s"}>
              {funnelBody}
              {funnelFooter}
            </Box>
          </MotionFlex>
        ) : (
          <MotionFlex
            key="menu"
            {...menuAnimationVariants}
            transition={transitionConfig}
            className="styled-scrollbar"
            flexGrow={1}
            overflowY="auto"
            maxWidth="100%"
            flexDirection="column"
          >
            {releases && (
              <ReleaseInfoCoverCarousel
                releases={releases}
                activeReleaseUuid={releaseUuid}
                setActiveReleaseUuid={(uuid) => {
                  openReleaseInfoMenu(
                    uuid,
                    activeRelease?.comic?.slug ?? "",
                    {},
                    true,
                  );
                }}
              />
            )}
            {!activeRelease || isLoading || isLoadingReleases ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                width="100%"
                position="absolute"
                top="0"
                left="0"
              >
                <Spinner color="blaze.blaze" />
              </Flex>
            ) : (
              <Flex
                flexDirection="column"
                pt="16px"
                px="24px"
                pb="24px"
                gap="16px"
              >
                <ReleaseInfoHeader
                  comic={comic}
                  release={activeRelease as Release}
                />
                <Divider color="transparent.white.10" mt="8px" />
                <ReleaseInfoActions
                  setFunnelConfig={openPaywall}
                  contextArea={FunnelContextArea.releaseDetails}
                  release={activeRelease as Release}
                />
                <Divider color="transparent.white.10" />
                <ReleaseInfoPageCarousel release={activeRelease as Release} />
                {hasBundles ? (
                  <Tabs variant="line" colorScheme="blaze">
                    <TabList>
                      <UnderlinedTab fontSize="16px">
                        {t("components.slideInMenu.bundleDetails.details")}
                      </UnderlinedTab>
                      <UnderlinedTab fontSize="16px">
                        <Flex alignItems="center" gap={2}>
                          <Text>
                            {t("components.slideInMenu.bundleDetails.bundles")}
                          </Text>
                          <NewBadge />
                        </Flex>
                      </UnderlinedTab>
                    </TabList>
                    <TabPanels>
                      <TabPanel px={0}>
                        <Flex flexDirection="column" gap="16px">
                          <ComicLabelsCarousel comicData={comic} />
                          <Divider color="transparent.white.10" />
                          <ShowMoreSection
                            collapsedHeight={150}
                            expandedMarginBottom="24px"
                          >
                            <Text
                              fontFamily="Roboto"
                              fontSize="14px"
                              fontWeight="400"
                              lineHeight="22.4px"
                              color="neutral.300"
                            >
                              {activeRelease.description}
                            </Text>
                          </ShowMoreSection>
                          {comic.artist && (
                            <ArtistSection artist={comic.artist} />
                          )}
                          {comic.contributors && (
                            <>
                              <Divider color="transparent.white.10" />
                              <ComicContributorList comicData={comic} byRole />
                            </>
                          )}
                        </Flex>
                      </TabPanel>
                      <TabPanel px={0}>
                        <SlideInMenuBundlesList
                          bundles={bundles}
                          text={t(
                            "components.slideInMenu.comicDetailsMenu.bundlesContainingComic",
                          )}
                        />
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                ) : (
                  <Flex flexDirection="column" gap="16px">
                    <ComicLabelsCarousel comicData={comic} />
                    <Divider color="transparent.white.10" />
                    <ShowMoreSection
                      collapsedHeight={150}
                      expandedMarginBottom="24px"
                    >
                      <Text
                        fontFamily="Roboto"
                        fontSize="14px"
                        fontWeight="400"
                        lineHeight="22.4px"
                        color="neutral.300"
                      >
                        {activeRelease.description}
                      </Text>
                    </ShowMoreSection>
                    {comic.artist && <ArtistSection artist={comic.artist} />}
                    {comic.contributors && (
                      <>
                        <Divider color="transparent.white.10" />
                        <ComicContributorList comicData={comic} byRole />
                      </>
                    )}
                  </Flex>
                )}
              </Flex>
            )}
          </MotionFlex>
        )}
      </AnimatePresence>
    </Flex>
  );
}
